import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { GiCommercialAirplane } from 'react-icons/gi';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { fetchDomesticPackages, fetchInternationalPackages } from '../utils/api';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_BASE_URL } from '../utils/api';

const BookNowModal = ({ open, onClose, selectedCategory }) => {
  const [phone, setPhone] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    destination: '',
    checkIn: '',
    checkOut: '',
    resortType: '',
    pax: '',
    child: '',
    rooms: '',
    mealPlan: '',
    inclusions: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDestinations = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const [domesticData, internationalData] = await Promise.all([
          fetchDomesticPackages(),
          fetchInternationalPackages(),
        ]);

        const allItems = [...domesticData, ...internationalData];

        const categories = allItems
          .filter((item) => item.type === 'category')
          .map((item) => ({ id: item._id, name: item.name, type: 'category', subcategories: [] }));

        const subcategories = allItems.filter((item) => item.type === 'subcategory');
        subcategories.forEach((item) => {
          if (item.category && item.category._id) {
            const categoryIndex = categories.findIndex((cat) => cat.id === item.category._id);
            if (categoryIndex !== -1) {
              categories[categoryIndex].subcategories.push({ id: item._id, name: item.title });
            } else {
              console.warn(
                `No category found for subcategory: ${item.title}, category ID: ${item.category._id}`,
              );
            }
          } else {
            console.warn('Invalid subcategory or missing category:', item);
          }
        });

        setDestinations(categories);
      } catch (error) {
        console.error('Error fetching destinations:', error);
        setFetchError('Failed to load destinations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, []);

  const filteredDestinations = useMemo(() => {
    if (!selectedCategory) {
      return destinations.flatMap((dest) =>
        dest.subcategories.length > 0
          ? dest.subcategories.map((sub) => ({ id: sub.id, name: sub.name }))
          : [{ id: dest.id, name: dest.name }],
      );
    }

    const isSubcategory = selectedCategory.type === 'subcategory';
    if (isSubcategory) {
      return [{ id: selectedCategory._id, name: selectedCategory.title }];
    }

    const category = destinations.find((dest) => dest.id === selectedCategory._id);
    if (category) {
      return category.subcategories.length > 0
        ? category.subcategories.map((sub) => ({ id: sub.id, name: sub.name }))
        : [{ id: category.id, name: category.name }];
    }

    const fallbackName = selectedCategory.title || selectedCategory.name;
    return fallbackName ? [{ id: selectedCategory._id, name: fallbackName }] : [];
  }, [selectedCategory, destinations]);

  useEffect(() => {
    if (open && selectedCategory) {
      const destinationName = selectedCategory.title || selectedCategory.name || '';

      const isValidDestination = filteredDestinations.some((dest) => dest.name === destinationName);
      setFormData({
        fullName: '',
        email: '',
        destination: isValidDestination ? destinationName : '',
        checkIn: '',
        checkOut: '',
        resortType: '',
        pax: '',
        child: '',
        rooms: '',
        mealPlan: '',
        inclusions: '',
        message: '',
      });
      setPhone('');
      setErrors({});
      setShowSuccessMessage(false);
      setCaptchaVerified(false);
      // setIsSubmitting(false);
    }
  }, [open, selectedCategory, filteredDestinations]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'fullName') {
      if (/\d/.test(value)) return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
    if (value) {
      setErrors((prev) => ({ ...prev, captcha: '' }));
    }
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (/\d/.test(formData.fullName)) {
      newErrors.fullName = 'Full name cannot contain numbers';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!phone) newErrors.phone = 'WhatsApp number is required';

    if (!formData.destination) newErrors.destination = 'Destination is required';

    if (!captchaVerified) newErrors.captcha = 'Please verify that you are not a robot';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setIsSubmitting(true); // Show spinner
      try {
        const response = await fetch(`${API_BASE_URL}/api/bookings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...formData, phone }),
        });

        if (response.ok) {
          setShowSuccessMessage(true);
        } else {
          const errorData = await response.json();
          setErrors({ ...errors, api: errorData.message || 'Failed to submit. Please try again.' });
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors({ ...errors, api: 'An error occurred. Please try again.' });
      } finally {
        setIsSubmitting(false); // Hide spinner
      }
    }
  };

  const inputClass = 'border p-3 rounded w-full h-12';
  const labelClass = () => `text-sm mb-1 block text-gray-700`;

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
    >
      <DialogTitle className='bg-gray-100 p-4 text-center text-xl font-bold'>
        {showSuccessMessage ? 'Thank You!' : 'Book Now'}
      </DialogTitle>
      <hr />
      <DialogContent>
        {showSuccessMessage ? (
          <div className='flex flex-col items-center justify-center p-8'>
            <svg
              className='mb-4 h-16 w-16 text-green-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
              ></path>
            </svg>
            <p className='text-center text-lg font-semibold text-gray-700'>
              Your request has been submitted successfully. We'll get back to you soon!
            </p>
          </div>
        ) : (
          <div className='mt-2 grid grid-cols-1 gap-4 md:grid-cols-3'>
            <div className='w-full'>
              <label className={labelClass()}>Full Name*</label>
              <input
                type='text'
                name='fullName'
                value={formData.fullName}
                onChange={handleChange}
                className={inputClass}
              />
              {errors.fullName && <p className='mt-1 text-sm text-red-500'>{errors.fullName}</p>}
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Email*</label>
              <input
                type='email'
                name='email'
                value={formData.email}
                onChange={handleChange}
                className={inputClass}
              />
              {errors.email && <p className='mt-1 text-sm text-red-500'>{errors.email}</p>}
            </div>

            <div className='w-full'>
              <label className={labelClass()}>WhatsApp Number*</label>
              <PhoneInput
                country={'in'}
                value={phone}
                onChange={setPhone}
                inputClass='!w-full !h-12 !text-base'
                containerClass='!w-full'
                specialLabel=''
              />
              {errors.phone && <p className='mt-1 text-sm text-red-500'>{errors.phone}</p>}
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Destination*</label>
              <div className='relative'>
                <select
                  name='destination'
                  value={formData.destination}
                  onChange={handleChange}
                  className={`${inputClass} appearance-none pr-10`}
                  disabled={loading || fetchError}
                >
                  <option value=''>{loading ? 'Loading...' : 'Select Destination'}</option>
                  {fetchError ? (
                    <option
                      value=''
                      disabled
                    >
                      Error loading destinations
                    </option>
                  ) : filteredDestinations.length === 0 && selectedCategory ? (
                    <option
                      value=''
                      disabled
                    >
                      No destinations available
                    </option>
                  ) : (
                    filteredDestinations.map((dest) => (
                      <option
                        key={dest.id}
                        value={dest.name}
                      >
                        {dest.name}
                      </option>
                    ))
                  )}
                </select>
                <svg
                  className='pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </div>
              {errors.destination && (
                <p className='mt-1 text-sm text-red-500'>{errors.destination}</p>
              )}
              {fetchError && <p className='mt-1 text-sm text-red-500'>{fetchError}</p>}
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Check In Date</label>
              <input
                type='date'
                name='checkIn'
                value={formData.checkIn}
                onChange={handleChange}
                className={inputClass}
                min={getTodayDate()}
              />
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Check Out Date</label>
              <input
                type='date'
                name='checkOut'
                value={formData.checkOut}
                onChange={handleChange}
                className={inputClass}
                min={formData.checkIn || getTodayDate()}
              />
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Resort Type</label>
              <div className='relative'>
                <select
                  name='resortType'
                  value={formData.resortType}
                  onChange={handleChange}
                  className={`${inputClass} appearance-none pr-10`}
                >
                  <option value=''>Select Resort Type</option>
                  <option value='5 Star'>5 Star</option>
                  <option value='4 Star'>4 Star</option>
                  <option value='3 Star'>3 Star</option>
                  <option value='Other'>Other</option>
                </select>
                <svg
                  className='pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </div>
            </div>

            <div className='w-full'>
              <label className={labelClass()}>No of Pax</label>
              <input
                type='text'
                name='pax'
                value={formData.pax}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className='w-full'>
              <label className={labelClass()}>No of Child</label>
              <input
                type='text'
                name='child'
                value={formData.child}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className='w-full'>
              <label className={labelClass()}>No of Rooms</label>
              <input
                type='text'
                name='rooms'
                value={formData.rooms}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Meal Plan</label>
              <input
                type='text'
                name='mealPlan'
                value={formData.mealPlan}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className='w-full'>
              <label className={labelClass()}>Inclusions</label>
              <input
                type='text'
                name='inclusions'
                value={formData.inclusions}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className='col-span-1 grid w-full grid-cols-1 gap-4 md:col-span-3 md:grid-cols-2'>
              <div className='w-full'>
                <label className={labelClass()}>Message</label>
                <textarea
                  rows={3}
                  name='message'
                  value={formData.message}
                  onChange={handleChange}
                  className={`${inputClass} h-auto`}
                />
              </div>
              <div className='mt-6 w-full items-center'>
                <ReCAPTCHA
                  sitekey='6LcmktYrAAAAAEg0JVdKaKO_s9cqoQQP2QI5iiRs'
                  onChange={handleCaptchaChange}
                />
                {errors.captcha && <p className='mt-1 text-sm text-red-500'>{errors.captcha}</p>}
              </div>
            </div>
          </div>
        )}
        {errors.api && <p className='mt-4 text-center text-red-500'>{errors.api}</p>}
      </DialogContent>
      <DialogActions className='p-4'>
        <Button
          onClick={onClose}
          variant='outlined'
          disabled={isSubmitting}
        >
          Close
        </Button>
        {!showSuccessMessage && (
          <Button
            variant='contained'
            className='bg-gradient-to-r from-indigo-500 to-green-400 px-6 text-white'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className='flex items-center'>
                <div className='mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent text-white'></div>
                Submitting...
              </div>
            ) : (
              'Submit'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BookNowModal;
